import React from 'react';
import Amount from './Amount';

/**
 * Reusable Shipping Method Selector Component
 * 
 * Displays shipping method options with radio/checkbox selection
 * 
 * @param {Array} shippingMethods - Array of shipping method objects
 * @param {string} selectedMethod - Currently selected method service_code
 * @param {Function} onMethodChange - Callback when method is selected
 * @param {boolean} showLocalPickup - Whether to show local pickup option
 * @param {boolean} showTitle - Whether to show "Shipping Method" title
 * @param {string} variant - Display variant: 'order' (default) or 'checkout'
 * @param {Object} customFreight - Optional custom freight shipping option
 */
const ShippingMethodSelector = ({
  shippingMethods = [],
  selectedMethod,
  onMethodChange,
  showLocalPickup = true,
  showTitle = true,
  variant = 'order', // 'order' or 'checkout'
  customFreight = null, // { show: boolean, rate: number, label: string }
}) => {
  
  // Normalize service_type display
  const normalizeServiceType = (serviceType) => {
    if (serviceType === 'UPS® Ground') return 'UPS Ground®';
    return serviceType;
  };

  return (
    <div className={variant === 'order' ? 'dimensionShow_div' : undefined}>
      {/* Column 1: Shipping Methods */}
      <div className={variant === 'order' ? 'flex-grow-1' : undefined}>
        {showTitle && (
          <h4 className="ShippingMethodmain_title">
            Shipping Method
          </h4>
        )}
        
        {/* Local Pickup Option */}
        {showLocalPickup && (
          <div className={variant === 'checkout' ? 'rate-option' : 'dimensionShowCheckbox_div'}>
            {variant === 'checkout' ? (
              <label>
                <input
                  type="checkbox"
                  value="local_pickup"
                  checked={selectedMethod === "local_pickup" || selectedMethod === "Local Pickup (FREE)"}
                  onChange={() => onMethodChange("local_pickup")}
                />
                <b>&nbsp;&nbsp;Local Pickup (FREE)</b>
              </label>
            ) : (
              <>
                <input
                  type="checkbox"
                  id="localPickup"
                  checked={selectedMethod === "local_pickup" || selectedMethod === "Local Pickup (FREE)"}
                  onChange={() => onMethodChange("local_pickup")}
                />
                <label htmlFor="localPickup">
                  Local Pickup (FREE)
                </label>
              </>
            )}
          </div>
        )}

        {/* Custom Freight Option */}
        {customFreight?.show && customFreight?.rate !== 0 && (
          <div style={variant === 'order' ? { marginBottom: '10px' } : undefined}>
            <div className={variant === 'checkout' ? 'rate-option' : 'dimensionShowCheckbox_div'}>
              {variant === 'checkout' ? (
                <label className="d-flex align-items-start gap-2">
                  <input
                    type="checkbox"
                    value="custom_rates"
                    checked={selectedMethod === "custom_rates"}
                    onChange={() => onMethodChange("custom_rates")}
                  />
                  <div className="flex-grow-1 mb-2">
                    <b>{customFreight.label || 'Freight Shipping'}</b>
                    {' '}-{' '}
                    <Amount amount={customFreight.rate} />
                  </div>
                </label>
              ) : (
                <>
                  <input
                    type="checkbox"
                    id="customFreight"
                    checked={selectedMethod === "custom_rates"}
                    onChange={() => onMethodChange("custom_rates")}
                  />
                  <label htmlFor="customFreight">
                    {customFreight.label || 'Freight Shipping'} (
                    <Amount amount={customFreight.rate} />
                    )
                  </label>
                </>
              )}
            </div>
            {variant === 'order' && (
              <div style={{ marginLeft: '25px', marginTop: '-10px' }}>
                <small style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                  Delivery time will be calculated later
                </small>
              </div>
            )}
          </div>
        )}

        {/* Shipping Methods */}
        {shippingMethods?.map((method) => {
          // Hide UPS methods with $0 price (exclude local_pickup and custom_rates)
          const isUPSMethod = method.service_code &&
                             !['local_pickup', 'custom_rates'].includes(method.service_code);
          const hasZeroPrice = method.shipping_amount === 0 || method.shipping_amount === '0';

          if (isUPSMethod && hasZeroPrice) {
            return null; // Hide this method
          }

          return (
            <div key={method.service_code} style={variant === 'order' ? { marginBottom: '10px' } : undefined}>
              <div className={variant === 'checkout' ? 'rate-option' : 'dimensionShowCheckbox_div'}>
                {variant === 'checkout' ? (
                  <label className="d-flex align-items-start gap-2">
                    <input
                      type="checkbox"
                      value={method.service_code}
                      checked={selectedMethod === method.service_code}
                      onChange={() => onMethodChange(method.service_code)}
                    />
                    <div className="flex-grow-1">
                      <b>{normalizeServiceType(method.service_type)}</b>
                      {' '}-{' '}
                      <Amount amount={method.shipping_amount} />
                      <br />
                      {method.delivery_days ? (
                        <small style={{ display: 'block' }}>
                          Estimated Delivery: {method.delivery_days} Business day{method.delivery_days > 1 ? 's' : ''}
                        </small>
                      ) : (
                        <small style={{ display: 'block', color: '#999' }}>
                          Delivery time will be calculated later
                        </small>
                      )}
                    </div>
                  </label>
                ) : (
                  <>
                    <input
                      type="checkbox"
                      id={method.service_code}
                      checked={selectedMethod === method.service_code}
                      onChange={() => onMethodChange(method.service_code)}
                    />
                    <label htmlFor={method.service_code}>
                      {normalizeServiceType(method.service_type)} (
                      <Amount amount={method.shipping_amount} />
                      )
                    </label>
                  </>
                )}
              </div>
              {variant === 'order' && (
                <div style={{ marginLeft: '25px', marginTop: '-10px' }}>
                  {method.delivery_days ? (
                    <small style={{ fontSize: '12px', color: '#666' }}>
                      Estimated Delivery: {method.delivery_days} Business day{method.delivery_days > 1 ? 's' : ''}
                    </small>
                  ) : (
                    <small style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                      Delivery time will be calculated later
                    </small>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShippingMethodSelector;
